import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IStationeryItem,
  IStationeryItemHistory,
  IStationeryItemListItem,
  StationeryItemStatus,
} from 'src/types/stationery-item';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  items: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryItemListItem[];
  };
  item: IStationeryItem | null;
  history: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IStationeryItemHistory | null;
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  items: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  item: null,
  history: {
    status: 'idle',
    error: undefined,
    data: null,
  },
};

const stationeryItemSlice = createSlice({
  name: 'stationeryItem',
  initialState,
  reducers: {
    removeStationeryItem: (state, action) => {
      const { itemId } = action.payload;

      const index = state.items.items.findIndex((item) => item._id === itemId);

      if (index > -1) {
        action.payload.index = index;
        state.items.items = state.items.items.filter((item) => item._id !== itemId);
      }
    },
    rollbackStationeryItemDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.items.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryItems.fulfilled, (state, action) => {
        state.status = 'success';
        state.items = action.payload;
      })
      .addCase(getStationeryItems.rejected, (state, action) => {
        state.items = initialState.items;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryItemById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryItemById.fulfilled, (state, action) => {
        state.status = 'success';
        state.item = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryItemById.rejected, (state, action) => {
        state.item = initialState.item;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryItemHistory.pending, (state) => {
        state.history.status = 'loading';
      })
      .addCase(getStationeryItemHistory.fulfilled, (state, action) => {
        state.history.status = 'success';
        state.history.data = action.payload;
        state.history.error = undefined;
      })
      .addCase(getStationeryItemHistory.rejected, (state, action) => {
        state.history.status = 'failure';
        state.history.error = action.error.message;
      });
  },
});

export default stationeryItemSlice.reducer;

export const { removeStationeryItem, rollbackStationeryItemDeletion } =
  stationeryItemSlice.actions;

export const getStationeryItems = createAsyncThunk(
  'stationeryItem/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    itemCode?: string | null;
    itemName?: string | null;
    minQuantity?: number | null;
    maxQuantity?: number | null;
    units?: string[];
    unitsSeparator?: ',' | '|';
    alertEnabled?: string | null;
    status?: string | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const {
      page,
      limit,
      generalSearch,
      itemCode,
      itemName,
      minQuantity,
      maxQuantity,
      units,
      unitsSeparator,
      alertEnabled,
      status,
      sortBy,
      sort,
    } = params;

    try {
      const response = await axios.get(
        `stationery/item?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&itemCode=${itemCode ?? ''}&itemName=${itemName ?? ''}&minQuantity=${
          minQuantity ?? ''
        }&maxQuantity=${maxQuantity ?? ''}&unit=${
          units ? units.join(unitsSeparator ?? ',') : ''
        }&alertEnabled=${alertEnabled ?? ''}&status=${status ?? ''}&sortBy=${sortBy}&sort=${
          sort ?? ''
        }`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryItemById = createAsyncThunk(
  'stationeryItem/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/item/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryItemHistory = createAsyncThunk(
  'stationeryItem/history',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/item/history/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryItem = createAsyncThunk(
  'stationeryItem/delete',
  async (params: { item: IStationeryItemListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`stationery/item/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryItemDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TCreateStationeryItem {
  itemName: string;
  initialQuantity?: number | null;
  suppliers?: string[];
  unit: string;
  reorderThreshold?: number | null;
  alertEnabled?: boolean;
}

export interface TUpdateStationeryItem extends Omit<TCreateStationeryItem, 'initialQuantity'> {
  status?: StationeryItemStatus;
}

export const createStationeryItem = createAsyncThunk(
  'stationeryItem/create',
  async (params: { newItem: TCreateStationeryItem }) => {
    const { newItem } = params;
    const response = await axios.post('stationery/item', newItem);
    return response.data.data;
  }
);

export const updateStationeryItem = createAsyncThunk(
  'stationeryItem/update',
  async (params: { itemId: string; updatingItem: TUpdateStationeryItem }) => {
    const { itemId, updatingItem } = params;
    const response = await axios.put(`stationery/item/${itemId}`, updatingItem);
    return response.data.data;
  }
);

export const adjustStationeryItemQuantity = createAsyncThunk(
  'stationeryItem/adjustQuantity',
  async (params: { itemId: string; newQuantity: number; reason?: string }) => {
    const { itemId, newQuantity, reason } = params;
    const response = await axios.patch(`stationery/item/${itemId}/adjust-quantity`, {
      newQuantity,
      reason,
    });
    return response.data.data;
  }
);
